// Shipping reads use the existing HttpOnly login cookie. No token storage or bearer headers.
export class ShippingApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function get(path, params, signal) {
  const query = new URLSearchParams(params || {}).toString();
  const response = await fetch(`/api/shipping${path}${query ? `?${query}` : ""}`, {
    method: "GET", credentials: "same-origin", cache: "no-store",
    headers: { Accept: "application/json" }, signal,
  });
  let data;
  try { data = await response.json(); } catch (_) {
    throw new ShippingApiError("The Shipping service returned an unreadable response.", response.status);
  }
  if (!response.ok) {
    throw new ShippingApiError(data.error?.message || "Shipping could not be read.", response.status, data.error?.code);
  }
  return data;
}

export const shippingApi = {
  profitPostage: (orderId, signal) => get(`/orders/${encodeURIComponent(orderId)}/profit-postage`, null, signal),
  profitPostageCommand: (orderId, action, payload, csrfToken) =>
    shippingApi.postageCommand(`orders/${encodeURIComponent(orderId)}/profit-postage/${action}`, payload, csrfToken),
  postage: (orderId, signal) => get(`/orders/${encodeURIComponent(orderId)}/postage`, null, signal),
  postageCommand: async (path, payload, csrfToken) => {
    const response = await fetch(`/api/shipping/${path}`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Expense outcome unknown. Retry this same request or reload the ledger.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Expense could not be recorded.', response.status, data.error?.code);
    return data;
  },
  batches: (signal) => get('/packing/batches', null, signal),
  batch: (key, signal) => get(`/packing/batches/${key}`, null, signal),
  stationState: (jobId, signal) => get('/packing/station-state', jobId ? { seen_job_id: jobId } : null, signal),
  dispatch: (shipmentId, signal) => get(`/shipments/${shipmentId}/dispatch`, null, signal),
  dispatchCommand: async (path, payload, csrfToken) => {
    const response = await fetch(`/api/shipping/${path}`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Dispatch command outcome unknown. Retry the same request or reload.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Dispatch command failed.', response.status, data.error?.code);
    return data;
  },
  buyerMessagePreview: (operationKey, signal) => get(`/operations/${encodeURIComponent(operationKey)}/buyer-message`, null, signal),
  buyerMessagePhoto: async (operationKey, evidenceKey, signal) => {
    const response = await fetch(`/api/shipping/operations/${encodeURIComponent(operationKey)}/buyer-message/photos/${encodeURIComponent(evidenceKey)}`, {
      method: 'GET', credentials: 'same-origin', cache: 'no-store', signal,
    });
    if (!response.ok) throw new ShippingApiError('Buyer photo unavailable. Reload the review.', response.status);
    return response.blob();
  },
  queueBuyerMessage: async (operationKey, payload, csrfToken) => {
    const response = await fetch(`/api/shipping/operations/${encodeURIComponent(operationKey)}/queue-buyer-message`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Queue outcome unknown. Retry this same queue request.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Buyer message could not be queued.', response.status, data.error?.code);
    return data;
  },
  operations: (orderId, beforeId, signal) => get(`/orders/${encodeURIComponent(orderId)}/operations`,
    beforeId ? { before_id: beforeId } : null, signal),
  queueMedia: async (operationKey, payload, csrfToken, signal) => {
    const response = await fetch(`/api/shipping/operations/${encodeURIComponent(operationKey)}/queue-media`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Media queue outcome unknown. Retry with the same request.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Media upload could not be queued.', response.status, data.error?.code);
    return data;
  },
  suppressBuyerMessage: async (operationKey, payload, csrfToken) => {
    const response = await fetch(`/api/shipping/operations/${encodeURIComponent(operationKey)}/suppress-buyer-message`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Suppression outcome unknown. Retry this same request.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'This message cannot be suppressed.', response.status, data.error?.code);
    return data;
  },
  evidence: (jobId, signal) => get(`/packing/jobs/${jobId}/evidence`, null, signal),
  capture: (captureKey, signal) => get(`/packing/captures/${captureKey}`, null, signal),
  uploadEvidence: async (captureKey, file, metadata, csrfToken, signal) => {
    const body = new FormData();
    body.append('metadata', JSON.stringify(metadata));
    // Camera filenames can contain private data. Only a fixed transport name is sent.
    body.append('photo', file, 'packing-photo');
    const response = await fetch(`/api/shipping/packing/captures/${captureKey}/upload`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal,
      headers: { 'X-CSRF-Token': csrfToken }, body,
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Photo outcome unknown. Retry this capture.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Photo upload failed.', response.status, data.error?.code);
    return data;
  },
  visualChecks: (evidenceKey, signal) => get(`/packing/evidence/${evidenceKey}/visual-checks`, null, signal),
  qualityCheck: (evidenceKey, payload, csrfToken, signal) => shippingApi.packingCommand(`evidence/${evidenceKey}/quality-check`, payload, csrfToken, signal),
  visualCheck: (evidenceKey, payload, csrfToken, signal) => shippingApi.packingCommand(`evidence/${evidenceKey}/visual-check`, payload, csrfToken, signal),
  visualDisposition: (evidenceKey, checkKey, payload, csrfToken, signal) => shippingApi.packingCommand(`evidence/${evidenceKey}/visual-checks/${checkKey}/disposition`, payload, csrfToken, signal),
  evidenceFile: async (evidenceKey, variant, signal) => {
    const response = await fetch(`/api/shipping/packing/evidence/${evidenceKey}/${variant}`, {
      method: 'GET', credentials: 'same-origin', cache: 'no-store', signal,
    });
    if (!response.ok) throw new ShippingApiError('Private photo unavailable. Refresh your session and retry.', response.status);
    return response.blob();
  },
  printJobs: (jobId, signal) => get(`/packing/jobs/${jobId}/print-jobs`, null, signal),
  printJob: (id, signal) => get(`/print-jobs/${id}`, null, signal),
  printCommand: async (path, payload, csrfToken, signal) => {
    const response = await fetch(`/api/shipping/${path}`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Print request outcome unknown. Retry the same request.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Print request failed.', response.status, data.error?.code);
    return data;
  },
  labels: (jobId, signal) => get(`/packing/jobs/${jobId}/labels`, null, signal),
  labelFile: async (jobId, artifactKey, kind, format, signal) => {
    const response = await fetch(`/api/shipping/packing/jobs/${jobId}/labels/${artifactKey}/${kind}.${format}`, {
      method: 'GET', credentials: 'same-origin', cache: 'no-store', signal,
      headers: { Accept: format === 'pdf' ? 'application/pdf' : 'image/png' },
    });
    if (!response.ok) {
      let data;
      try { data = await response.json(); } catch (_) { /* No private response body in errors. */ }
      throw new ShippingApiError(data?.error?.message || 'Label unavailable. Reload the package.', response.status, data?.error?.code);
    }
    return response.blob();
  },
  packingCatalog: (signal) => get('/packing/catalog', null, signal),
  activeJob: (signal) => get('/packing/active-job', null, signal),
  verification: (jobId, signal) => get(`/packing/jobs/${jobId}/verification`, null, signal),
  scan: async (jobId, payload, csrfToken, signal) => {
    const response = await fetch(`/api/shipping/packing/jobs/${jobId}/scans`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Scan outcome unknown. Retry the same scan.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Scan failed.', response.status, data.error?.code);
    return data;
  },
  packingJob: (id, signal) => get(`/packing/jobs/${id}`, null, signal),
  packingWorkspace: (signal) => get('/packing/workspace', null, signal),
  packingOrder: (id, signal) => get(`/packing/orders/${encodeURIComponent(id)}`, null, signal),
  packingCommand: async (path, payload, csrfToken, signal) => {
    const response = await fetch(`/api/shipping/packing/${path}`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError('Command outcome unknown. Retry the same request.', response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || 'Packing command failed.', response.status, data.error?.code);
    return data;
  },
  capabilities: (signal) => get("/capabilities", null, signal),
  queue: (params, signal) => get("/queue", params, signal),
  detail: (orderId, signal) => get(`/orders/${encodeURIComponent(orderId)}`, null, signal),
  events: (orderId, beforeId, signal) => get(`/orders/${encodeURIComponent(orderId)}/events`,
    beforeId ? { before_id: beforeId } : null, signal),
  sourceHealth: (orderId, signal) => get("/source-health", orderId ? { order_id: orderId } : null, signal),
  requestSource: async (orderId, requestKey, csrfToken, signal, phase = 'PREPARATION') => {
    const path = orderId ? `/orders/${encodeURIComponent(orderId)}/source-check` : "/source-refresh";
    const payload = orderId ? { request_key: requestKey, phase } : { request_key: requestKey };
    const response = await fetch(`/api/shipping${path}`, {
      method: "POST", credentials: "same-origin", cache: "no-store", signal,
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken }, body: JSON.stringify(payload),
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new ShippingApiError("Source request outcome unknown. Retry with the same request.", response.status); }
    if (!response.ok) throw new ShippingApiError(data.error?.message || "Source request failed.", response.status, data.error?.code);
    return data;
  },
};
